import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TeamPageTemplateProps } from "./template";
import { useUser } from "@auth";
import { useFormik, FormikErrors } from "formik";
import { useAddUserToPremiumPartner } from "@mutations";
import { useGetPartnerPaymentMethods } from "@queries";
import { AuthContext } from "@auth";
// toast notifications removed per UX request
import * as XLSX from "xlsx";

type ImportPreviewStatus = "ready" | "error" | "skipped";
export type EmailLanguage = "en" | "fr";

type ImportPreviewRow = {
  lineNumber: number;
  firstName: string;
  lastName: string;
  email: string;
  employmentStartDate?: string;
  birthday?: string;
  status: ImportPreviewStatus;
  message?: string;
};

export interface EmployeeFormValues {
  firstName: string;
  lastName: string;
  emailAddress: string;
  language: EmailLanguage;
  employmentStartDate: string;
  birthday: string;
  autoSignUp: boolean;
}

/* Below type can be moved to a more appropriate types folder later and have more attributes added */
export type EmployeeAdditionProps = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  language: EmailLanguage;
  employmentStartDate: string;
  birthday: string;
  autoSignUp: boolean;
};

export const useTeamPage = (): TeamPageTemplateProps => {
  const { t } = useTranslation();
  const partner = useUser();
  const addUser = useAddUserToPremiumPartner();
  const { billingDetails, isLoadingData, setIsLoadingData, creditCardStatus } = useContext(AuthContext);
  const { getPartnerPaymentMethods } = useGetPartnerPaymentMethods();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);
  const [errorModalTitle, setErrorModalTitle] = useState<string | null>(null);
  const [emailNotAlreadyAdded, setEmailNotAlreadyAdded] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importCompletedCount, setImportCompletedCount] = useState(0);
  const [importTotalCount, setImportTotalCount] = useState(0);
  const [isParsingImport, setIsParsingImport] = useState(false);
  const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [importPreviewFileName, setImportPreviewFileName] = useState<string | null>(null);
  const [lastImportFileName, setLastImportFileName] = useState<string | null>(null);
  const [importAutoSignUp, setImportAutoSignUp] = useState(false);
  const [importLanguage, setImportLanguage] = useState<EmailLanguage>("en");
  const [importResults, setImportResults] = useState<
    {
      lineNumber: number;
      firstName: string;
      lastName: string;
      email: string;
      status: "success" | "duplicate" | "failure";
      message?: string;
      employmentStartDate?: string;
      birthday?: string;
    }[]
  >([]);
  const [importResultsFilter, setImportResultsFilter] = useState<
    "success" | "duplicate" | "failure"
  >("success");
  const [isImportResultsOpen, setIsImportResultsOpen] = useState(false);

  const pickDefaultResultsFilter = (
    results: {
      status: "success" | "duplicate" | "failure";
    }[],
  ): "success" | "duplicate" | "failure" => {
    const successCount = results.filter((r) => r.status === "success").length;
    const duplicateCount = results.filter((r) => r.status === "duplicate").length;
    const failureCount = results.filter((r) => r.status === "failure").length;
    if (successCount > 0) return "success";
    if (duplicateCount >= failureCount) return "duplicate";
    return "failure";
  };
  const cancelImportRef = useRef(false);
  const premiumUsers = partner?.premiumSettings?.[0]?.users || [];

  const isAddressSubmitted =
    partner?.address !== null && partner?.address !== undefined;
  const isCreditCardAdded = billingDetails?.creditCards === null || (billingDetails?.creditCards && billingDetails.creditCards.length > 0) ? true : false;
  const isCreditCardExpired = creditCardStatus === "expired";
  const closeErrorModal = () => {
    setErrorModalIsOpen(false);
    setErrorModalMessage(null);
    setErrorModalTitle(null);
  };

  const openErrorModal = (message: string, title?: string) => {
    setErrorModalMessage(message);
    setErrorModalTitle(title ?? t("teamPage.errors.genericImportTitle", "Import issue"));
    setErrorModalIsOpen(true);
  };
  
  useEffect(() => {
    // load the list of linked payment methods, only used to determine if the Add Team Member button is enabled
    getPartnerPaymentMethods();
  }, []);

  const onEditTeamMemberClicked = () => {
    alert(t("teamPage.modals.editTitle"));
  };

  const onManageNumberOfSeatsClicked = () => {};

  function closeModal() {
    setModalIsOpen(false);
  }

  const validate = (
    values: EmployeeFormValues,
  ): FormikErrors<EmployeeFormValues> => {
    const errors: FormikErrors<EmployeeFormValues> = {};
    if (!values.emailAddress) {
      errors.emailAddress = t("teamPage.errors.emailRequired");
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.emailAddress)
    ) {
      errors.emailAddress = t("teamPage.errors.emailInvalid");
    }
    if (!values.firstName) {
      errors.firstName = t("teamPage.errors.firstNameRequired");
    }
    if (!values.lastName) {
      errors.lastName = t("teamPage.errors.lastNameRequired");
    }
    return errors;
  };

  const { values, errors, handleChange, handleSubmit, setFieldError, setFieldValue } =
    useFormik<EmployeeFormValues>({
      initialValues: {
        firstName: "",
        lastName: "",
        emailAddress: "",
        language: "en",
        employmentStartDate: "",
        birthday: "",
        autoSignUp: false,
      },
      validate,
      validateOnChange: false,
      onSubmit: async (values: EmployeeAdditionProps) => {
        setIsLoadingData(true);
        try {
          console.log(values.firstName, values.lastName, values.emailAddress);
          const isUserAlreadyAdded = partner?.premiumSettings?.[0]?.users?.find(
            (u) => u.emailAddress === values.emailAddress,
          );
          if (isUserAlreadyAdded) {
            throw new Error("UserAlreadyAdded");
          }
          await addUser(
            values.firstName,
            values.lastName,
            values.emailAddress,
            billingDetails?.creditCards?.[0]?.paymentId ?? "",
            values.employmentStartDate,
            values.birthday,
            values.autoSignUp,
            values.language,
          );
          setEmailNotAlreadyAdded(true);
          setIsLoadingData(false);
          closeModal();
        } catch (error: any) {
          if (error?.message === "UserAlreadyAdded") {
            setFieldError(
              "emailAddress",
              t("teamPage.errors.emailAlreadyAdded"),
            );
            setEmailNotAlreadyAdded(false);
            setIsLoadingData(false);
            return;
          }

          const errorType =
            error?.graphQLErrors?.[0]?.errorType ||
            error?.graphQLErrors?.[0]?.extensions?.errorType ||
            error?.networkError?.result?.errors?.[0]?.errorType ||
            error?.networkError?.result?.errors?.[0]?.extensions?.errorType ||
            error?.errorType ||
            error?.extensions?.errorType ||
            (typeof error?.message === "string" &&
              error.message.includes("UserEmailAlreadyExists") &&
              "UserEmailAlreadyExists") ||
            (typeof error?.extensions?.code === "string" &&
              error.extensions.code === "UserEmailAlreadyExists" &&
              "UserEmailAlreadyExists");

          const messageCandidate =
            error?.graphQLErrors?.[0]?.message ||
            error?.networkError?.result?.errors?.[0]?.message ||
            error?.message ||
            error?.extensions?.message;

          const messageMatchesDuplicate =
            typeof messageCandidate === "string" &&
            messageCandidate.toLowerCase().includes("user") &&
            messageCandidate.toLowerCase().includes("email") &&
            messageCandidate.toLowerCase().includes("exist");

          if (errorType === "UserEmailAlreadyExists" || messageMatchesDuplicate) {
            const serverMessage =
              messageCandidate ||
              t(
                "teamPage.errors.emailAlreadyExistsGlobal",
                "A user with this email already exists in Coinmiles. Please use a different email address."
              );
            openErrorModal(
              serverMessage,
              t("teamPage.errors.emailAlreadyExistsGlobalTitle", "Email already in use"),
            );
            setIsLoadingData(false);
            return;
          }

          console.log("Unhandled add-user error", error);
          setIsLoadingData(false);
          closeModal();
        }
      },
    });

  const onAddTeamMemberClicked = () => {
    if (values) {
      values.firstName = "";
      values.lastName = "";
      values.emailAddress = "";
      values.language = "en";
      values.autoSignUp = false;
    }
    errors.emailAddress = ""; // Reset the possible email already added error
    setEmailNotAlreadyAdded(true);
    setErrorModalIsOpen(false);
    setErrorModalMessage(null);
    setErrorModalTitle(null);
    setIsLoadingData(false);
    setModalIsOpen(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setEmailNotAlreadyAdded(true);
    if (e.target.type === "checkbox") {
      setFieldValue(e.target.name, e.target.checked);
      return;
    }
    if (e.target.name === "emailAddress") {
      setFieldValue("emailAddress", e.target.value.toLowerCase());
      return;
    }
    handleChange(e);
  };

  type CsvRowKey =
    | "firstName"
    | "lastName"
    | "email"
    | "employmentStartDate"
    | "birthday";

  const headerAliases: Record<string, CsvRowKey> = {
    "first name": "firstName",
    firstname: "firstName",
    "last name": "lastName",
    lastname: "lastName",
    email: "email",
    "email address": "email",
    "employment start date": "employmentStartDate",
    "start date": "employmentStartDate",
    "employment date": "employmentStartDate",
    birthday: "birthday",
    "date of birth": "birthday",
    dob: "birthday",
  };

  const friendlyColumnName: Record<CsvRowKey, string> = {
    firstName: t("teamPage.modals.labels.firstName"),
    lastName: t("teamPage.modals.labels.lastName"),
    email: t("teamPage.modals.labels.email"),
    employmentStartDate: t("teamPage.modals.labels.workAnniversary"),
    birthday: t("teamPage.modals.labels.birthday"),
  };

  const parseCsvLine = (line: string): string[] => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        values.push(current);
        current = "";
      } else {
        current += char;
      }
    }

    values.push(current);
    return values;
  };

  const sanitizeDate = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }
    return parsed.toISOString().split("T")[0];
  };

  const resetImportPreview = () => {
    setIsImportPreviewOpen(false);
    setImportPreviewRows([]);
    setImportPreviewFileName(null);
    setImportAutoSignUp(false);
    setImportLanguage("en");
  };

  const processImportRows = (rows: string[][], _fileName: string | null): void => {
    if (!rows.length || !rows[0]) {
      openErrorModal(t("teamPage.errors.csvNoData"));
      resetImportPreview();
      return;
    }

    const headerCells = rows[0].map((cell) => (cell ?? "").toString());
    const indexMap: Partial<Record<CsvRowKey, number>> = {};

    headerCells.forEach((rawHeader, index) => {
      const normalized = rawHeader.trim().toLowerCase();
      const mappedKey = headerAliases[normalized];
      if (mappedKey && indexMap[mappedKey] === undefined) {
        indexMap[mappedKey] = index;
      }
    });

    const requiredKeys: CsvRowKey[] = ["firstName", "lastName", "email"];
    const missingColumns = requiredKeys.filter(
      (key) => indexMap[key] === undefined,
    );

    if (missingColumns.length) {
      openErrorModal(
        t("teamPage.errors.missingColumns", {
          columns: missingColumns.map((key) => friendlyColumnName[key]).join(", "),
        }),
      );
      resetImportPreview();
      return;
    }

    const paymentId = billingDetails?.creditCards?.[0]?.paymentId;
    if (!paymentId) {
      openErrorModal(t("teamPage.errors.missingPayment"));
      resetImportPreview();
      return;
    }

    const existingEmails = new Set(
      premiumUsers.map((user: any) =>
        (user.emailAddress ?? "").toLowerCase(),
      ),
    );

    const dataRows = rows
      .slice(1)
      .map((cells, idx) => ({
        cells: (cells ?? []).map((cell) =>
          cell === undefined || cell === null ? "" : String(cell),
        ),
        lineNumber: idx + 2,
      }))
      .filter(({ cells }) =>
        cells.some((cell) => (cell ?? "").toString().trim()),
      );

    if (!dataRows.length) {
      openErrorModal(t("teamPage.errors.noRowsToImport"));
      resetImportPreview();
      return;
    }

    const previewRows: ImportPreviewRow[] = [];
    const encounteredEmails = new Set(existingEmails);

    for (let i = 0; i < dataRows.length; i += 1) {
      const { cells, lineNumber } = dataRows[i];

      const getCell = (key: CsvRowKey): string => {
        const index = indexMap[key];
        if (index === undefined) {
          return "";
        }
        return (cells[index] ?? "").toString().trim();
      };

      const firstName = getCell("firstName");
      const lastName = getCell("lastName");
      const email = getCell("email");
      const employmentStartDate = sanitizeDate(
        getCell("employmentStartDate"),
      );
      const birthday = sanitizeDate(getCell("birthday"));

      if (!email) {
        previewRows.push({
          lineNumber,
          firstName,
          lastName,
          email: "",
          employmentStartDate,
          birthday,
          status: "error",
          message: t("teamPage.errors.missingEmail"),
        });
        continue;
      }

      const normalizedEmail = email.toLowerCase();
      if (existingEmails.has(normalizedEmail)) {
        previewRows.push({
          lineNumber,
          firstName,
          lastName,
          email,
          employmentStartDate,
          birthday,
          status: "error",
          message: t("teamPage.errors.userAlreadyOnTeam"),
        });
        continue;
      }

      if (encounteredEmails.has(normalizedEmail)) {
        previewRows.push({
          lineNumber,
          firstName,
          lastName,
          email,
          employmentStartDate,
          birthday,
          status: "error",
          message: t("teamPage.errors.duplicateInFile", { email }),
        });
        continue;
      }

      if (!firstName || !lastName) {
        previewRows.push({
          lineNumber,
          firstName,
          lastName,
          email,
          employmentStartDate,
          birthday,
          status: "error",
          message: t("teamPage.errors.missingNameForEmail", { email }),
        });
        continue;
      }

      previewRows.push({
        lineNumber,
        firstName,
        lastName,
        email,
        employmentStartDate,
        birthday,
        status: "ready",
        message: t("teamPage.importPreview.statusReady"),
      });
      encounteredEmails.add(normalizedEmail);
    }

    setImportPreviewRows(previewRows);
    const readyCount = previewRows.filter(
      (row) => row.status === "ready",
    ).length;
    setImportTotalCount(readyCount);
    if (!readyCount) {
      openErrorModal(t("teamPage.errors.noValidRows"));
    }
  };

  const onImportTeamCsv = async (file: File): Promise<void> => {
    const fileName = file.name ?? null;
    const extension = fileName?.split(".").pop()?.toLowerCase();
    const isExcel = extension === "xlsx" || extension === "xls";
    const isCsv = extension === "csv" || !extension;

    if (!isCsv && !isExcel) {
      openErrorModal(t("teamPage.errors.unsupportedFile"));
      return;
    }

    setImportPreviewFileName(fileName);
    setImportPreviewRows([]);
    setIsImportPreviewOpen(true);
    setIsParsingImport(true);
    setImportCompletedCount(0);
    setImportTotalCount(0);
    setImportResults([]);
    setIsImportResultsOpen(false);
    setLastImportFileName(fileName);

    try {
      let rows: string[][] = [];

      if (isExcel) {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const [firstSheet] = workbook.SheetNames;
        if (!firstSheet) {
          openErrorModal(t("teamPage.errors.csvNoData"));
          resetImportPreview();
          return;
        }
        const sheet = workbook.Sheets[firstSheet];
        rows = (XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        }) as string[][]).map((r) =>
          (r ?? []).map((cell) =>
            cell === undefined || cell === null ? "" : String(cell),
          ),
        );
        if (!rows.length) {
          openErrorModal(t("teamPage.errors.csvNoData"));
          resetImportPreview();
          return;
        }
      } else {
        const fileText = await file.text();
        if (!fileText.trim()) {
          openErrorModal(t("teamPage.errors.csvEmpty"));
          resetImportPreview();
          return;
        }

        const lines = fileText.split(/\r?\n/);
        if (!lines.length) {
          openErrorModal(t("teamPage.errors.csvNoData"));
          resetImportPreview();
          return;
        }

        rows = lines.map((line) => parseCsvLine(line));
      }

      processImportRows(rows, fileName);
    } catch (error) {
      console.error("Error importing team members from file", error);
      openErrorModal(t("teamPage.errors.csvReadError"));
      resetImportPreview();
    } finally {
      setIsParsingImport(false);
    }
  };

  const closeImportPreview = () => {
    if (isImporting) {
      return;
    }
    resetImportPreview();
  };

  const onConfirmImportPreview = async (): Promise<void> => {
    const paymentId = billingDetails?.creditCards?.[0]?.paymentId;
    if (!paymentId) {
      openErrorModal(t("teamPage.errors.missingPayment"));
      return;
    }

    const rowsToImport = importPreviewRows.filter(
      (row) => row.status === "ready",
    );

    if (!rowsToImport.length) {
      openErrorModal(t("teamPage.errors.noValidSelected"));
      return;
    }

    cancelImportRef.current = false;
    setIsImporting(true);
    setImportCompletedCount(0);
    setImportTotalCount(rowsToImport.length);
    setIsLoadingData(true);
    setIsImportPreviewOpen(false);

    let importedCount = 0;

    const existingEmails = new Set(
      premiumUsers.map((user: any) =>
        (user.emailAddress ?? "").toLowerCase(),
      ),
    );
    const collectedResults: typeof importResults = [];
    let combinedResults: typeof importResults = [];

    try {
      for (let i = 0; i < rowsToImport.length; i += 1) {
        if (cancelImportRef.current) {
          break;
        }

        const row = rowsToImport[i];
        const normalizedEmail = row.email.toLowerCase();

        if (existingEmails.has(normalizedEmail)) {
          collectedResults.push({
            lineNumber: row.lineNumber,
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            status: "duplicate",
            message: t("teamPage.errors.emailAlreadyAdded"),
            employmentStartDate: row.employmentStartDate,
            birthday: row.birthday,
          });
          continue;
        }

        try {
          await addUser(
            row.firstName,
            row.lastName,
            row.email,
            paymentId,
            row.employmentStartDate,
            row.birthday,
            importAutoSignUp,
            importLanguage,
          );
          existingEmails.add(normalizedEmail);
          importedCount += 1;
          setImportCompletedCount(importedCount);
          collectedResults.push({
            lineNumber: row.lineNumber,
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            status: "success",
            message: t("teamPage.importResults.successMessage", "Imported"),
            employmentStartDate: row.employmentStartDate,
            birthday: row.birthday,
          });
        } catch (error) {
          console.error("Failed to import team member from CSV", error);
          const errorMessage =
            (error as any)?.graphQLErrors?.[0]?.message ||
            (error as any)?.networkError?.result?.errors?.[0]?.message ||
            (error as any)?.message ||
            t("teamPage.toasts.rowImportFailed", {
              line: row.lineNumber,
              email: row.email,
            });

          const isDuplicateError =
            (error as any)?.graphQLErrors?.[0]?.errorType === "UserEmailAlreadyExists" ||
            (error as any)?.graphQLErrors?.[0]?.extensions?.errorType === "UserEmailAlreadyExists" ||
            (error as any)?.networkError?.result?.errors?.[0]?.errorType === "UserEmailAlreadyExists" ||
            (error as any)?.networkError?.result?.errors?.[0]?.extensions?.errorType === "UserEmailAlreadyExists" ||
            (error as any)?.errorType === "UserEmailAlreadyExists" ||
            (error as any)?.extensions?.errorType === "UserEmailAlreadyExists" ||
            (typeof (error as any)?.message === "string" &&
              (error as any).message.toLowerCase().includes("email") &&
              (error as any).message.toLowerCase().includes("exist"));

          collectedResults.push({
            lineNumber: row.lineNumber,
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            status: isDuplicateError ? "duplicate" : "failure",
            message: errorMessage,
            employmentStartDate: row.employmentStartDate,
            birthday: row.birthday,
          });
        }
      }

      const nonReadyResults = importPreviewRows
        .filter((row) => row.status !== "ready")
        .map((row) => {
          const message = row.message || t("teamPage.errors.noValidRows");
          const lowerMsg = message.toLowerCase();
          const isDup =
            lowerMsg.includes("duplicate") ||
            lowerMsg.includes("already") ||
            lowerMsg.includes("team");
          return {
            lineNumber: row.lineNumber,
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            status: isDup ? "duplicate" : "failure",
            message,
            employmentStartDate: row.employmentStartDate,
            birthday: row.birthday,
          };
        });

      combinedResults = [...collectedResults, ...nonReadyResults].sort(
        (a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0),
      );

    } finally {
      cancelImportRef.current = false;
    setIsImporting(false);
    setIsLoadingData(false);
    setImportResults(combinedResults);
    setImportResultsFilter(pickDefaultResultsFilter(combinedResults));
    setIsImportResultsOpen(true);
    resetImportPreview();
  }
};

  const onCancelImport = () => {
    if (isImporting) {
      cancelImportRef.current = true;
    }
  };

  const exportFailedCsv = () => {
    if (typeof document === "undefined") {
      return;
    }
    const failures = importResults.filter(
      (r) => r.status === "duplicate" || r.status === "failure",
    );
    if (!failures.length) {
      openErrorModal(
        t("teamPage.importResults.noFailuresToExport", "No failed rows to export."),
        t("teamPage.errors.genericImportTitle", "Import issue"),
      );
      return;
    }

    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Employment Start Date",
      "Birthday",
    ];

    const original = lastImportFileName ?? "team-import.csv";
    const originalParts = original.split(".");
    const originalExt =
      originalParts.length > 1 ? originalParts.pop()?.toLowerCase() : undefined;
    const exportExt =
      originalExt === "xlsx" || originalExt === "xls" ? "xlsx" : "csv";
    const base = originalParts.join(".") || "team-import";

    if (exportExt === "xlsx") {
      const aoa = [
        headers,
        ...failures.map((r) => [
          r.firstName,
          r.lastName,
          r.email,
          r.employmentStartDate,
          r.birthday,
        ]),
      ];
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const filename = `${base}_NO_IMPORT.xlsx`;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const toCsvValue = (value: string | null | undefined) => {
      if (value === undefined || value === null) return "";
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = failures.map((r) =>
      [
        r.firstName,
        r.lastName,
        r.email,
        r.employmentStartDate,
        r.birthday,
      ]
        .map(toCsvValue)
        .join(","),
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const filename = `${base}_NO_IMPORT.csv`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    premiumUsers,
    onAddTeamMemberClicked,
    onEditTeamMemberClicked,
    onManageNumberOfSeatsClicked,
    onImportTeamCsv,
    partner,
    handleInputChange,
    errors,
    values,
    handleSubmit,
    modalIsOpen,
    closeModal,
    isAddressSubmitted,
    isCreditCardAdded,
    creditCardStatus,
    creditCards: billingDetails?.creditCards,
    isLoadingData,
    setIsLoadingData,
    emailNotAlreadyAdded,
    isImporting,
    importCompletedCount,
    importTotalCount,
    isParsingImport,
    isImportPreviewOpen,
    importPreviewRows,
    importPreviewFileName,
    importAutoSignUp,
    importLanguage,
    setImportAutoSignUp,
    setImportLanguage,
    errorModalIsOpen,
    errorModalMessage,
    errorModalTitle,
    closeErrorModal,
    onConfirmImportPreview,
    closeImportPreview,
    onCancelImport,
    importResults,
    importResultsFilter,
    setImportResultsFilter,
    isImportResultsOpen,
    setIsImportResultsOpen,
    exportFailedCsv,
    lastImportFileName,
  };
};
